import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  APPLE_ADR,
  DEVICE_MEDIA,
  MEDIA_UPLOAD,
  VIDEO_STREAMING,
} from '@rsos/capstone/src/constants/capabilities';
import { parseWSMessage } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  DEVICE_MEDIA as DEVICE_MEDIA_ORG,
  setDeviceMediaData,
} from '@rsos/reduxDataStore/src/features/media/mediaSlice';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import {
  APPLE_ADR_PERMISSION,
  DEVICE_MEDIA_PERMISSION,
  MEDIA_UPLOAD_PERMISSION,
  VIDEO_STREAMING_PERMISSION,
} from '../../../../constants/permissions';

const useActiveCapabilities = () =>
  useSelector(state => state.psaps?.currentPSAP?.activeCapabilities);
// Falls back to an empty array so the hooks below can call includes() directly.
// The selector returns undefined when the user's profile is unavailable, which
// denies access here — the safe direction for a permission check.
const usePermissions = () =>
  useSelector(
    state => selectUserPermissionsByApp(state?.sinatra?.user, 'capstone') ?? [],
  );

/**
 * Hook to determine if user has the ability to access the Request ADR button
 */
export const useHasAccessToRequestAdr = () => {
  const activeCapabilities = useActiveCapabilities();
  const permissions = usePermissions();

  const allowedCapabilities = [APPLE_ADR];
  const allowedPermissions = [APPLE_ADR_PERMISSION];

  let hasCapabilityAccess = false,
    hasPermissionEnabled = false;
  allowedCapabilities.forEach(c => {
    if (activeCapabilities?.[c]) {
      hasCapabilityAccess = true;
    }
  });

  allowedPermissions.forEach(p => {
    if (permissions.includes(p)) {
      hasPermissionEnabled = true;
    }
  });

  return hasCapabilityAccess && hasPermissionEnabled;
};

/**
 * Hook to determine if user has the ability to access the Display Media button
 */
export const useHasAccessToDisplayMedia = () => {
  const activeCapabilities = useActiveCapabilities();
  const permissions = usePermissions();
  return (
    !!activeCapabilities[DEVICE_MEDIA] &&
    permissions.includes(DEVICE_MEDIA_PERMISSION)
  );
};

/**
 * Hook to determine if user has the ability to access the Live Video button
 */
export const useHasAccessToLiveVideo = () => {
  const activeCapabilities = useActiveCapabilities();
  const permissions = usePermissions();
  return (
    !!activeCapabilities[VIDEO_STREAMING] &&
    permissions.includes(VIDEO_STREAMING_PERMISSION)
  );
};

/**
 * Hook to determine if user has the ability to access the Request Media button
 */
export const useHasAccessToRequestMedia = () => {
  const activeCapabilities = useActiveCapabilities();
  const permissions = usePermissions();
  return (
    !!activeCapabilities[MEDIA_UPLOAD] &&
    permissions.includes(MEDIA_UPLOAD_PERMISSION)
  );
};

/**
 * Hook that accesses Hermes to handle Media data through the use of an
 * additional callback function. Assumes user has access to the data, otherwise
 * it won't be sent through Hermes
 */
export const useHandleMedia = () => {
  const Hermes = window.__RSOS?.hermes?.instance;

  const dispatch = useDispatch();

  const handleMedia = useCallback(
    message => {
      const parsedMessageType = parseWSMessage(message);
      if (parsedMessageType.organization === DEVICE_MEDIA_ORG) {
        dispatch(setDeviceMediaData(message));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (Hermes) {
      Hermes.addOnMessageCallback({ handleMedia: handleMedia });

      return () => {
        Hermes.removeOnMessageCallback('handleMedia');
      };
    }
  }, [Hermes, handleMedia]);

  return null;
};

/**
 * Remove duplicate objects in an array by creating a new list of objects
 * with sorted keys, then stringified and added to a Set to handle the
 * duplicates, then create a new array and parse the stringified data to return
 * as JSON
 * @param {Array} data - List of objects
 */
const removeDuplicateObjects = data => {
  if (!Array.isArray(data)) {
    return [];
  }
  const newData = data
    .map(d => {
      const sortedData = {};

      const sortedKeys = Object.keys(d).sort();
      sortedKeys.forEach(k => {
        sortedData[k] = d[k];
      });
      return sortedData;
    })
    .map(JSON.stringify);

  const set = new Set(newData);
  const normalized = Array.from(set).map(JSON.parse);
  return normalized;
};
/**
 * Hook that returns the media upload data for a callerId
 * @param {String} callerId - phone number
 */
export const useSelectedMediaData = callerId => {
  const data =
    useSelector(state => state?.media?.data?.media?.[callerId]?.mediaUpload) ||
    [];

  return removeDuplicateObjects(data) || [];
};

/**
 * Hook that returns the video data for a callerId
 * @param {String} callerId - phone number
 */
export const useSelectedVideoData = callerId => {
  const data = useSelector(state => state?.media?.data?.video?.[callerId]);
  return data || {};
};

/**
 * Hook that returns a boolean for whether or not a call has ended
 * @param {String} callerId - phone number
 */
export const useHasCallEnded = callerId => {
  if (!callerId) {
    throw Error(`callerId not supplied`);
  }
  const hasCallEnded = useSelector(state => {
    const callLocations =
      state.emergencyCalls.callQueue?.[callerId]?.core?.locations;

    if (callLocations && callLocations.length) {
      return !!callLocations[callLocations.length - 1]?.call_end_time;
    }
    // Call isn't in the queue. Assume call ended
    return true;
  });
  return hasCallEnded;
};
